import { fetchWithTokenRefresh } from './fetchWrapper';
import type { ApiResponse } from './api';
import type {
  ExpertTipListResponse,
  SubmitExpertTipFeedbackResponse,
  ExpertTipUserFeedbackResponse,
  FeedbackType,
} from '../types/expert-tip.types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export const expertTipsApi = {
  /**
   * Get paginated list of published expert tips.
   * Uses fetchWithTokenRefresh for optional auth (sends token if available).
   */
  async getExpertTips(
    page: number = 1,
    pageSize: number = 20,
    timezone: string = 'UTC'
  ): Promise<ExpertTipListResponse> {
    try {
      const response = await fetchWithTokenRefresh(
        `${API_URL}/expert-tips?page=${page}&pageSize=${pageSize}&timezone=${encodeURIComponent(timezone)}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const result: ApiResponse<ExpertTipListResponse> = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please try logging in again.');
        }
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to fetch expert tips';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from expert tips');
      }

      return result.data;
    } catch (error) {
      console.error('Failed to fetch expert tips:', error);
      throw error;
    }
  },

  /**
   * Submit or update feedback for an expert tip.
   */
  async submitFeedback(
    expertTipId: number,
    feedbackType: FeedbackType
  ): Promise<SubmitExpertTipFeedbackResponse> {
    try {
      const response = await fetchWithTokenRefresh(
        `${API_URL}/expert-tips/${expertTipId}/feedback`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ feedbackType }),
        }
      );

      const result: ApiResponse<SubmitExpertTipFeedbackResponse> = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please try logging in again.');
        }
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to submit feedback';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from feedback submission');
      }

      return result.data;
    } catch (error) {
      console.error('Expert tip feedback submission failed:', error);
      throw error;
    }
  },

  /**
   * Get user's current feedback for an expert tip.
   */
  async getUserFeedback(expertTipId: number): Promise<ExpertTipUserFeedbackResponse> {
    try {
      const response = await fetchWithTokenRefresh(
        `${API_URL}/expert-tips/${expertTipId}/feedback`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const result: ApiResponse<ExpertTipUserFeedbackResponse> = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please try logging in again.');
        }
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to fetch feedback';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from feedback query');
      }

      return result.data;
    } catch (error) {
      console.error('Failed to fetch expert tip feedback:', error);
      throw error;
    }
  },
};
